import { ApiClient } from "@ucl/library";
import {
  FinancialApplication,
  FinancialApplicationCoreData,
  FinancialProductReadDTO,
} from "../types/FinancialApplication";
import {
  ShareApplicationPeopleResponse,
  GenerateInviteRequest,
  GenerateInviteResponse,
  SendInviteEmailRequest,
  LinkUserRequest,
} from "@/types/ShareApplication";
import { RunSanctionsMatchRequestDTO } from "@/types/RunSanctionsMatchRequestDTO";
import { Disclosure } from "../types/Disclosure";
import { ConsumerPortalMetrics } from "../types/ConsumerPortal";
import {
  ProcessPlaidIdentityVerificationRequest,
  ProcessPlaidIdentityVerificationResponse,
} from "../types/ProcessPlaidIdentityVerification";
import { ApplicationUserInviteReadDTO } from "@/types/ApplicationUserInviteReadDTO";
import { AppUser } from "@/contexts/UserContext/types";

interface DepositApplicationToggleActiveStatusDTO {
  depositApplicationId: string;
  isActive: boolean;
}

class FinancialApplicationApiClient {
  private _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  getFinancialApplication = async (financialApplicationId: string) => {
    return this._apiClient.get<FinancialApplication>(
      `api/financial-application/${financialApplicationId}`
    );
  };

  getFinancialApplicationCoreData = async (financialApplicationId: string) => {
    return this._apiClient.get<FinancialApplicationCoreData>(
      `api/financial-application/${financialApplicationId}/core`
    );
  };

  createFinancialApplication = async () => {
    return this._apiClient.post<FinancialApplicationCoreData>(`api/financial-application`);
  };

  createFinancialApplicationAsBanker = async () => {
    return this._apiClient.post<FinancialApplicationCoreData>(`api/financial-application/banker`);
  };

  createSeedFinancialApplication = async () => {
    return this._apiClient.post<FinancialApplication>(`api/financial-application/seed`);
  };

  attachUser = async (financialApplicationId: string) => {
    return this._apiClient.post<FinancialApplicationCoreData>(
      `api/financial-application/${financialApplicationId}/attach-user`
    );
  };

  submitFinancialApplication = async (financialApplicationId: string) => {
    return this._apiClient.post<FinancialApplicationCoreData>(
      `api/financial-application/${financialApplicationId}/submit`
    );
  };

  getFinancialProducts = async () => {
    return this._apiClient.get<FinancialProductReadDTO[]>(`api/FinancialProduct`);
  };

  getCompanyAssignees = async () => {
    return this._apiClient.get<AppUser[]>(`api/financial-application/company-assignees`);
  };

  updateFinancialApplication = async (
    financialApplicationId: string,
    data: FinancialApplication
  ) => {
    return this._apiClient.put(`api/financial-application/${financialApplicationId}`, data);
  };

  updateFinancialApplicationCompanyAssignee = async (
    financialApplicationId: string,
    companyAssigneeId: string
  ) => {
    return this._apiClient.put(`api/financial-application/${financialApplicationId}/company-assignee`, { companyAssigneeId });
  };

  updateFinancialApplicationDocuments = async (
    financialApplicationId: string,
    data: FinancialApplication
  ) => {
    return this._apiClient.put(
      `api/financial-application/${financialApplicationId}/documents`,
      data
    );
  };

  // Deposit Application methods
  toggleDepositApplicationActiveStatus = async (
    financialApplicationId: string,
    data: DepositApplicationToggleActiveStatusDTO
  ) => {
    return this._apiClient.put<FinancialApplicationCoreData>(
      `api/financial-application/${financialApplicationId}/deposit-application/toggle-active-status`,
      data
    );
  };

  /**
   * Gets all financial applications for the current user
   */
  getUserApplications = async () => {
    return this._apiClient.get<FinancialApplicationCoreData[]>(
      `api/financial-application/user/applications`
    );
  };

  /**
   * Gets the list of people that the application can be shared with
   */
  getShareApplicationPeople = async (financialApplicationId: string) => {
    return this._apiClient.get<ShareApplicationPeopleResponse>(
      `api/financial-application/${financialApplicationId}/share-people`
    );
  };

  /**
   * Generates an invite link for a person to access the financial application
   */
  generateInviteLink = async (financialApplicationId: string, request: GenerateInviteRequest) => {
    return this._apiClient.post<GenerateInviteResponse>(
      `api/financial-application/${financialApplicationId}/generate-invite-link`,
      request
    );
  };

  /**
   * Sends an invite email to a person for the financial application
   */
  sendInviteEmail = async (financialApplicationId: string, request: SendInviteEmailRequest) => {
    return this._apiClient.post(
      `api/financial-application/${financialApplicationId}/send-invite-link`,
      request
    );
  };

  /**
   * Links an existing user to a person in the financial application
   */
  linkUser = async (financialApplicationId: string, request: LinkUserRequest) => {
    return this._apiClient.post<FinancialApplicationCoreData>(
      `api/financial-application/${financialApplicationId}/link-user`,
      request
    );
  };

  /**
   * Runs sanctions match on a specific entity within a financial application.
   */
  runSanctionsMatch = async (
    financialApplicationId: string,
    request: RunSanctionsMatchRequestDTO
  ) => {
    return this._apiClient.post(
      `api/financial-application/${financialApplicationId}/run-sanctions-match`,
      request
    );
  };

  agreeToDisclosure = async (
    financialApplicationId: string,
    disclosureId: string
  ): Promise<Disclosure> => {
    return this._apiClient.put<Disclosure>(
      `api/financial-application/${financialApplicationId}/disclosure-acknowledgement/${disclosureId}/agree`
    );
  };

  /**
   * Gets consumer portal metrics for the current user
   */
  getConsumerPortalMetrics = async (): Promise<ConsumerPortalMetrics> => {
    return this._apiClient.get<ConsumerPortalMetrics>(
      `api/financial-application/consumer-portal/metrics`
    );
  };

  /**
   * Processes Plaid identity verification for a financial application
   */
  processPlaidIdentityVerification = async (
    financialApplicationId: string,
    request: ProcessPlaidIdentityVerificationRequest
  ): Promise<ProcessPlaidIdentityVerificationResponse> => {
    return this._apiClient.post<ProcessPlaidIdentityVerificationResponse>(
      `api/financial-application/${financialApplicationId}/process-identity-verification`,
      request
    );
  };

  /**
   * Renews an expired invite
   */
  renewInvite = async (inviteId: string): Promise<ApplicationUserInviteReadDTO> => {
    return this._apiClient.post<ApplicationUserInviteReadDTO>(
      `api/financial-application/renew-invite/${inviteId}`
    );
  };

  /**
   * Sends a credit authorization email to the applicant
   */
  sendCreditAuthorizationEmail = async (
    financialApplicationId: string,
    request: { creditAuthorizationId: string }
  ): Promise<void> => {
    return this._apiClient.post<void>(
      `api/financial-application/${financialApplicationId}/send-credit-authorization-email`,
      request
    );
  };
}

export default FinancialApplicationApiClient;
